import { supabase } from '../supabase';
import { Recommendation } from '../types';
import { v4 as uuidv4 } from 'uuid';

/**
 * Serviço para gerenciar feedback de usuários sobre recomendações
 */
export class FeedbackService {
  /**
   * Marca uma recomendação como visualizada
   */
  public async markRecommendationAsViewed(recommendationId: string): Promise<boolean> {
    try {
      if (!recommendationId) {
        console.warn('ID de recomendação vazio');
        return false;
      }
      
      // Verificar primeiro se a recomendação existe
      const { data: recommendation, error: checkError } = await supabase
        .from('recommendations')
        .select('id, viewed')
        .eq('id', recommendationId)
        .maybeSingle(); // Usar maybeSingle em vez de single para evitar erros 406
      
      if (checkError) {
        console.error('Erro ao verificar recomendação:', checkError);
        return false;
      }
      
      // Se a recomendação não existe, não fazer nada
      if (!recommendation) {
        console.warn('Recomendação não encontrada:', recommendationId);
        return false;
      }
      
      // Se já foi visualizada, não precisa atualizar
      if (recommendation.viewed) {
        return true;
      }
      
      // Atualizar para visualizada
      const { error: updateError } = await supabase
        .from('recommendations')
        .update({ viewed: true })
        .eq('id', recommendationId);
      
      if (updateError) {
        console.error('Erro ao marcar recomendação como visualizada:', updateError);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Exceção ao marcar recomendação como visualizada:', error);
      return false;
    }
  }

  /**
   * Registra feedback do usuário para uma recomendação
   */
  public async giveRecommendationFeedback(
    recommendationId: string, 
    liked: boolean | null,
    recommendation?: any
  ): Promise<any> {
    try {
      if (!recommendationId) {
        console.error('ID de recomendação inválido');
        return null;
      }
      
      // Verificar se a recomendação existe
      const { data: existingRec, error: checkError } = await supabase
        .from('recommendations')
        .select('*')
        .eq('id', recommendationId)
        .maybeSingle();
      
      // Se ocorrer um erro ao verificar
      if (checkError) {
        console.error('Erro ao verificar existência da recomendação:', checkError);
        return null;
      }
      
      // Se a recomendação não existe e temos dados, verificar se já existe feedback para o mesmo jogo
      if (!existingRec && recommendation) {
        // Validar recomendação
        if (!recommendation.user_id || !recommendation.game_id) {
          console.error('Recomendação inválida para salvar feedback');
          return null;
        }
        
        // NOVO: Verificar se já existe alguma recomendação para este jogo e usuário
        const { data: existingGameRec, error: gameCheckError } = await supabase
          .from('recommendations')
          .select('*')
          .eq('user_id', recommendation.user_id)
          .eq('game_id', recommendation.game_id)
          .maybeSingle();
          
        if (gameCheckError) {
          console.error('Erro ao verificar recomendações existentes para o jogo:', gameCheckError);
        }
        
        // Se já existe uma recomendação para este jogo, atualizar em vez de criar nova
        if (existingGameRec) {
          console.log(`Feedback para o jogo ${recommendation.game_id} já existe, atualizando em vez de criar nova recomendação`);
          
          const { data: updatedRec, error: updateError } = await supabase
            .from('recommendations')
            .update({ 
              liked: liked,
              viewed: true,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingGameRec.id)
            .select('*')
            .single();
            
          if (updateError) {
            console.error('Erro ao atualizar feedback existente:', updateError);
            return null;
          }
          
          return updatedRec;
        }
        
        // Criar recomendação com feedback
        const { data: newRec, error: createError } = await supabase
          .from('recommendations')
          .insert({
            ...recommendation,
            liked: liked,
            viewed: true
          })
          .select('*')
          .single();
        
        if (createError) {
          console.error('Erro ao criar recomendação com feedback:', createError);
          return null;
        }
        
        return newRec;
      }
      
      // Recomendação existe, verificar se o feedback é diferente
      if (existingRec) {
        if (existingRec.liked === liked && liked !== null) {
          // Feedback já existe e é o mesmo
          console.warn('Feedback já registrado anteriormente com o mesmo valor', existingRec);
          
          // Remover feedback se clicar no mesmo botão novamente
          if (liked !== null) {
            console.log('Removendo feedback existente por clique no mesmo botão');
            
            const { data: clearedRec, error: clearError } = await supabase
              .from('recommendations')
              .update({ 
                liked: null,
                updated_at: new Date().toISOString()
              })
              .eq('id', recommendationId)
              .select('*')
              .single();
              
            if (clearError) {
              console.error('Erro ao remover feedback existente:', clearError);
            } else {
              return clearedRec;
            }
          }
          
          return existingRec;
        }
      }
      
      // Atualizar feedback
      const { data: updatedRec, error: updateError } = await supabase
        .from('recommendations')
        .update({ 
          liked: liked,
          viewed: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', recommendationId)
        .select('*')
        .single();
      
      if (updateError) {
        console.error('Erro ao atualizar feedback da recomendação:', updateError);
        return null;
      }
      
      return updatedRec;
    } catch (error) {
      console.error('Exceção ao registrar feedback de recomendação:', error);
      return null;
    }
  }

  /**
   * Obtém recomendações com um feedback específico
   */
  public async getFeedbackRecommendations(
    userId: string, 
    liked: boolean
  ): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('recommendations')
        .select('*, game:games(*)')
        .eq('user_id', userId)
        .eq('liked', liked)
        .order('updated_at', { ascending: false });
        
      if (error) {
        console.error('Erro ao obter recomendações com feedback:', error);
        return [];
      }
      
      return data || [];
    } catch (error) {
      console.error('Exceção ao obter recomendações com feedback:', error);
      return [];
    }
  }

  /**
   * Remove feedback de uma recomendação
   */
  public async removeFeedback(recommendationId: string): Promise<any | null> {
    try {
      const { data, error } = await supabase
        .from('recommendations')
        .update({ liked: null })
        .eq('id', recommendationId)
        .select('*')
        .single();
        
      if (error) {
        console.error('Erro ao remover feedback:', error);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Exceção ao remover feedback:', error);
      return null;
    }
  }

  /**
   * Rastreia cliques em links de afiliados
   */
  public async trackAffiliateClick(
    storeProductId: string, 
    userId?: string, 
    recommendationId?: string
  ): Promise<void> {
    try {
      const clickData: Record<string, any> = {
        store_product_id: storeProductId
      };
      
      // Adicionar ID do usuário se disponível
      if (userId) {
        clickData.user_id = userId;
      }
      
      // Adicionar ID da recomendação se disponível
      if (recommendationId) {
        clickData.recommendation_id = recommendationId;
      }
      
      // Registrar clique
      const { error } = await supabase
        .from('affiliate_clicks')
        .insert(clickData);
        
      if (error) {
        console.error('Erro ao registrar clique em afiliado:', error);
      }
    } catch (error) {
      console.error('Exceção ao registrar clique em afiliado:', error);
    }
  }
} 